// src/components/charts/core/ChartCard.tsx
/**
 * ChartCard - Card wrapper for charts with consistent styling
 * 
 * Features:
 * - Header with title, subtitle, icon, and actions
 * - Built-in loading skeleton support with multiple variants
 * - Asset selector slot
 * - Consistent dark theme styling
 */

import React from "react";
import ChartSkeleton, { type LoadingVariant } from "./ChartSkeleton";

export interface ChartCardProps {
  title?: string;
  subtitle?: string | undefined;
  headerIcon?: React.ReactNode;
  actions?: React.ReactNode;
  height?: number;
  children: React.ReactNode;
  /** Shows skeleton loading state when true */
  isLoading?: boolean;
  /** Skeleton variant to show when loading (default: 'chart') */
  loadingVariant?: LoadingVariant;
  /** Optional asset selector to show below the title */
  assetSelector?: React.ReactNode;
  /** Additional className for the card container */
  className?: string;
}

const ChartCard: React.FC<ChartCardProps> = ({
  title,
  subtitle,
  headerIcon,
  actions,
  height,
  children,
  isLoading = false,
  loadingVariant = "chart",
  assetSelector,
  className = "",
}) => {
  // Check if this card should use flex layout to fill height
  const useFlexLayout = className.includes("flex-col");

  return (
    <div className={`bg-gray-800/30 rounded-xl p-4 ${className}`}>
      {isLoading ? (
        <ChartSkeleton variant={loadingVariant} height={height} />
      ) : (
        <>
          {(title || subtitle || headerIcon || actions) && (
            <div className="flex items-center justify-between mb-2 flex-shrink-0">
              <div className="flex items-center gap-2 min-w-0">
                {headerIcon}
                <div className="min-w-0">
                  {title && <div className="text-xs uppercase tracking-wide text-slate-300 truncate">{title}</div>}
                  {subtitle && <div className="text-[11px] text-slate-400 truncate">{subtitle}</div>}
                </div>
              </div>
              {actions && (
                <div className="flex items-center gap-2">
                  {actions}
                </div>
              )}
            </div>
          )}
          {/* Asset selector row (below header) */}
          {assetSelector && (
            <div className="mb-3 pb-2 border-b border-slate-700/30 flex-shrink-0">
              {assetSelector}
            </div>
          )}
          <div
            style={height ? { height } : undefined}
            className={`${height ? "overflow-hidden" : ""} ${useFlexLayout ? "flex-1 min-h-0" : ""}`.trim() || undefined}
          >
            {children}
          </div>
        </>
      )}
    </div>
  );
};

export default ChartCard;
